'use client';

import React, { useEffect, useRef, useState } from 'react';

const ContactSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = entry.target.querySelectorAll('.reveal-on-scroll');
            reveals.forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-32 bg-parchment overflow-hidden"
    >
      {/* Decorative glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-clay/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-corsasia/8 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left — copy */}
          <div>
            <p className="reveal-on-scroll text-clay text-xs uppercase tracking-widest font-sans font-medium mb-6">
              Contactez-nous
            </p>
            <h2
              className="reveal-on-scroll font-serif italic text-charcoal leading-tight mb-8"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
            >
              Une question ?<br />
              Écrivez-nous.
            </h2>
            <p className="reveal-on-scroll font-sans text-charcoal/60 text-lg leading-relaxed mb-10 max-w-md">
              Notre équipe est à votre écoute. Que ce soit pour une question sur nos menus, une réservation de groupe ou un événement privé, nous vous répondons rapidement.
            </p>

            {/* Contact info */}
            <div className="reveal-on-scroll space-y-6">
              {[
                { icon: '📍', title: 'Nos adresses', desc: '6 points de vente en Corse — Ajaccio, Bastia, Porto-Vecchio' },
                { icon: '📧', title: 'Email', desc: 'contact@corsasia.fr' },
                { icon: '📞', title: 'Téléphone', desc: 'Du lundi au dimanche, de 9h à 22h' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <span className="text-2xl mt-0.5">{item.icon}</span>
                  <div>
                    <p className="font-sans font-semibold text-charcoal text-sm mb-1">{item.title}</p>
                    <p className="font-sans text-charcoal/50 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="reveal-on-scroll">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-4xl p-8 lg:p-10 shadow-2xl shadow-charcoal/10 border border-clay/10"
            >
              <h3 className="font-serif italic text-charcoal text-2xl mb-8">
                Envoyez-nous un message
              </h3>

              {status === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-serif text-charcoal text-xl mb-2">Message envoyé !</p>
                  <p className="font-sans text-charcoal/50 text-sm">Nous vous répondrons dans les plus brefs délais.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Name & Email */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                        Votre nom
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Nom complet"
                        className="form-input w-full rounded-xl px-4 py-3 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="vous@exemple.com"
                        className="form-input w-full rounded-xl px-4 py-3 text-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Phone & Subject */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="06 XX XX XX XX"
                        className="form-input w-full rounded-xl px-4 py-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                        Sujet
                      </label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="form-input w-full rounded-xl px-4 py-3 text-sm"
                      >
                        <option value="">Sélectionner</option>
                        <option value="reservation">Réservation</option>
                        <option value="groupe">Groupe / Événement</option>
                        <option value="feedback">Feedback</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                      Message
                    </label>
                    <textarea
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Votre message..."
                      className="form-input w-full rounded-xl px-4 py-3 text-sm resize-none leading-relaxed"
                      required
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="mt-4 w-full bg-corsasia text-parchment py-4 rounded-full font-sans font-semibold text-sm uppercase tracking-widest hover:bg-corsasia-dark transition-all duration-300 shadow-lg shadow-corsasia/20 disabled:opacity-50"
                  >
                    {status === 'loading' ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>

                  {status === 'error' && (
                    <p className="text-center text-red-500 text-sm mt-2">
                      Une erreur est survenue. Veuillez réessayer.
                    </p>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;